import { defineStore } from 'pinia'
import DomainManagementStore, {
  DomainManagementState,
} from '@/modules/domainManagement/types/store/DomainManagementStore'
import DomainManagementApi from '@/modules/domainManagement/service'
import {
  DomainRenewPayload,
  CreateDNSRecordPayload,
  CreatePrivateNameserverPayload,
  DeleteDNSRecordPayload,
  DeletePrivateNameserverPayload,
  EditDNSRecordPayload,
  TogglePayload,
  TogglePayloadAutorenewStatus,
  UpdateContactInformationPayloadRequest,
  UpdateNameServersPayloadRequest,
  UpdatePrivateNameserverPayloadIP,
  UpdateAdditionalFieldsPayload,
  RetryRegistrationPayload,
} from '@/modules/domainManagement/types/api/DomainManagementPayloads'
import DomainItem from '@/modules/myServices/entities/DomainItem'
import { getLocalStorageItem } from '@/utils'
import { DomainManagementRecord } from '@/modules/domainManagement/types/api/DomainManagementRecordsResponse'
import { DomainManagementAdditionalField } from '@/modules/domainManagement/types/api/DomainManagementAdditionalFieldsResponse'
import { DomainRetryRegistrationResponse } from '@/modules/domainManagement/types/api/DomainManagementRegistrationError'
import { DomainManagementOwnerChange } from '@/modules/domainManagement/types/api/DomainManagementOwnerChange'

const apiService = new DomainManagementApi()
const COUNTRY_DOMAINS = ['.eu', '.de', '.se', '.nu', '.it', '.es', '.com', '.us']

function extractRecordKeysWithErrors(errors: Record<string, string[]> | undefined): Set<number> {
  const errorKeys = Object.keys(errors || {})
  return new Set(
    errorKeys
      .map(key => {
        const match = key.match(/^records\.(\d+)\./)
        return match ? parseInt(match[1]) : null
      })
      .filter((k): k is number => k !== null),
  )
}

export const useDomainManagementStore: DomainManagementStore = defineStore('domainManagement', {
  state: () =>
    ({
      lockStatus: '',
      eppCode: '',
      nameServers: {},
      privateNameServers: {},
      domain: {},
      autorenewStatus: '',
      records: [],
      recordsOriginal: {},
      counts: {},
      templates: {},
      isSupportedRegistrantLock: true,
      recordsErrors: {},
      hasLocalChanges: false,
    }) as DomainManagementState,
  getters: {
    getCountLimit(state) {
      let used = 0,
        limit = 0
      const re = /^\s*(\d+)\s*\/\s*(\d+)\s*$/
      for (const val of Object.values(state.counts)) {
        const m = re.exec(val)
        if (!m) continue
        used += Number(m[1])
        limit += Number(m[2])
      }
      return `${used}/${limit}`
    },
    getDomainName(state) {
      return state.domain.domainname
    },
    getExpiryDomainDate(state) {
      return state.domain.expirydate
    },
    isCountryDomain(state) {
      return COUNTRY_DOMAINS.some(domain => state.domain.domainname && state.domain.domainname.endsWith(domain))
    },
    getEPPCodeString(state) {
      return state.eppCode
    },
    getNameServersCollection(state) {
      return state.nameServers ?? {}
    },
  },
  actions: {
    async toggleRegisterStatus(payload: TogglePayload) {
      const response = await apiService.toggleRegisterStatus(payload)
      return response
    },

    async getLockStatus(domainid: number) {
      const response = await apiService.getLockStatus(domainid)
      this.lockStatus = response.lockstatus
    },

    async getEPPCode(domainid: number) {
      const response = await apiService.getEPPCode(domainid)
      this.eppCode = response.eppcode
    },

    async getNameServers(domainid: number) {
      const response = await apiService.getNameServers(domainid)
      this.nameServers = response
    },

    async getPrivateNameServers(domainid: number) {
      const response = await apiService.getPrivateNameServers(domainid)
      this.privateNameServers = response.message
    },

    async updateNameServers(payload: UpdateNameServersPayloadRequest) {
      await apiService.updateNameServers(payload)
    },

    async getDomain(domainid: number) {
      const response = await apiService.getDomain(domainid)
      this.domain = new DomainItem(response.domain)
    },

    async toggleAutorenewStatus(payload: TogglePayloadAutorenewStatus) {
      const response = await apiService.toggleAutorenewStatus(payload)
      return response
    },

    async getAutorenewStatus(domainid: number) {
      const response = await apiService.getAutorenewStatus(domainid)
      this.autorenewStatus = response.autorenew
    },

    async resendVerificationEmail(domainid: number) {
      await apiService.resendVerificationEmail(domainid)
    },

    async getOwnerChange(domainid: number) {
      const response = await apiService.getOwnerChange(domainid)
      return (response?.ownerChange ?? null) as DomainManagementOwnerChange | null
    },

    async resendOwnerChangeEmail(domainid: number) {
      await apiService.resendOwnerChangeEmail(domainid)
    },

    async cancelOwnerChange(domainid: number) {
      await apiService.cancelOwnerChange(domainid)
    },

    async createPrivateNameServer(payload: CreatePrivateNameserverPayload) {
      await apiService.createPrivateNameServer(payload)
    },

    async updatePrivateNameServerIP(payload: UpdatePrivateNameserverPayloadIP) {
      await apiService.updatePrivateNameServerIP(payload)
    },

    async deletePrivateNameServer(payload: DeletePrivateNameserverPayload) {
      await apiService.deletePrivateNameServer(payload)
    },

    async getDomainRenewPrices(payload: DomainRenewPayload) {
      const response = await apiService.getDomainRenewPrices(payload)
      return response.pricing.renew
    },

    async getClientInfo(domainid: number) {
      const response = await apiService.getClientInfo(domainid)
      return response.contactInfo
    },

    async saveContactInfo(payload: UpdateContactInformationPayloadRequest) {
      await apiService.saveContactInfo(payload)
    },

    /**
     * The registrar is the authority on these values, so the fields it answers with are what the
     * form must show afterwards — never the values that were typed in.
     */
    async updateAdditionalFields(payload: UpdateAdditionalFieldsPayload) {
      const response = await apiService.updateAdditionalFields(payload)
      return (response?.fields ?? []) as DomainManagementAdditionalField[]
    },

    /**
     * The new registration attempt runs inside this request, so the answer is its outcome -
     * `registrationError` is null when the registrar accepted it.
     */
    async retryRegistration(payload: RetryRegistrationPayload) {
      const response = await apiService.retryRegistration(payload)
      return {
        status: response?.status ?? '',
        registrationError: response?.registrationError ?? null,
      } as DomainRetryRegistrationResponse
    },

    async getDNSRecords(domainId: number) {
      const response = await apiService.getDNSRecords({ domainId, domainName: getLocalStorageItem('domain') ?? '' })
      this.templates = response.templates
      this.recordsOriginal = Array.isArray(response.records) ? {} : response.records
      this.recordsErrors = response.errors

      const recordKeysWithErrors = extractRecordKeysWithErrors(response.errors)

      const out: Array<DomainManagementRecord> = []
      for (const key of Object.keys(response.records)) {
        const r = response.records[key]

        out.push({
          line: r.line,
          name: r.name,
          type: r.type,
          ttl: r.ttl,
          value: r.value,
          key: +key,
          isRecordWithError: recordKeysWithErrors.has(+key),
        })
      }

      this.records = out
      this.counts = response.counts
      this.hasLocalChanges = false
    },

    async deleteDNSRecord(payload: DeleteDNSRecordPayload) {
      await apiService.deleteDNSRecord(payload)

      await this.getDNSRecords(payload.domainId)
    },

    async editDNSRecord(payload: EditDNSRecordPayload) {
      await apiService.editDNSRecord(payload)

      await this.getDNSRecords(payload.domainId)
    },

    async createDNSRecord(payload: CreateDNSRecordPayload) {
      await apiService.createDNSRecord(payload)

      await this.getDNSRecords(payload.domainId)
    },

    async checkTransferLockSupport(tld: string) {
      const response = await apiService.checkTransferLockSupport(tld)
      this.isSupportedRegistrantLock = response.data.isSupported
    },

    clearDomain() {
      this.domain = {} as DomainItem
    },

    updateRecordLocally(
      recordIndex: string,
      updatedRecord: { line: string; name: string; type: string; ttl: string | number; value: any },
    ) {
      this.hasLocalChanges = true

      if (this.recordsOriginal && this.recordsOriginal[recordIndex]) {
        this.recordsOriginal[recordIndex] = {
          ...this.recordsOriginal[recordIndex],
          ...updatedRecord,
        }
      }

      if (this.recordsErrors) {
        const errorPrefix = `records.${recordIndex}.`
        const errorsToDelete = Object.keys(this.recordsErrors).filter(key => key.startsWith(errorPrefix))
        errorsToDelete.forEach(key => {
          delete (this.recordsErrors as Record<string, string[]>)[key]
        })

        // If no record-specific errors remain, clear generic "records" key too
        const hasRemainingRecordErrors = Object.keys(this.recordsErrors).some(key => /^records\.\d+\./.test(key))
        if (!hasRemainingRecordErrors) {
          this.recordsErrors = {}
        }
      }

      const recordKeysWithErrors = extractRecordKeysWithErrors(this.recordsErrors as Record<string, string[]>)
      const idx = this.records.findIndex(r => r.key === +recordIndex)
      if (idx !== -1) {
        this.records[idx] = {
          ...this.records[idx],
          name: updatedRecord.name,
          type: updatedRecord.type,
          ttl: updatedRecord.ttl,
          value: updatedRecord.value,
          isRecordWithError: recordKeysWithErrors.has(+recordIndex),
        }
      }
    },

    addRecordLocally(
      recordIndex: string,
      newRecord: { line: string; name: string; type: string; ttl: string | number; value: any },
    ) {
      this.hasLocalChanges = true

      if (this.recordsOriginal) {
        ;(this.recordsOriginal as Record<string, any>)[recordIndex] = newRecord
      }

      const records: Array<DomainManagementRecord> = this.records
      records.push({
        line: newRecord.line,
        name: newRecord.name,
        type: newRecord.type,
        ttl: newRecord.ttl,
        value: newRecord.value,
        key: +recordIndex,
        isRecordWithError: false,
      })
    },

    deleteRecordLocally(recordKey: string) {
      this.hasLocalChanges = true

      if (this.recordsOriginal) {
        delete (this.recordsOriginal as Record<string, any>)[recordKey]

        if (this.recordsErrors) {
          const errorPrefix = `records.${recordKey}.`
          const errorsToDelete = Object.keys(this.recordsErrors).filter(key => key.startsWith(errorPrefix))
          errorsToDelete.forEach(key => {
            delete (this.recordsErrors as Record<string, string[]>)[key]
          })

          const hasRemainingRecordErrors = Object.keys(this.recordsErrors).some(key => /^records\.\d+\./.test(key))
          if (!hasRemainingRecordErrors) {
            this.recordsErrors = {}
          }
        }
      }

      this.records = this.records.filter(r => r.key !== +recordKey)
    },

    async submitAllRecords(domainId: number, zoneName: string) {
      if (!this.recordsOriginal) return

      try {
        await apiService.editDNSRecord({
          domainId,
          zoneName,
          records: this.recordsOriginal,
        })

        await this.getDNSRecords(domainId)
      } catch (e: any) {
        if (e && e.formErrors) {
          const rawErrors: Record<string, string[]> = {}
          Object.keys(e.formErrors).forEach((key: string) => {
            rawErrors[key] = [e.formErrors[key].message]
          })

          this.recordsErrors = rawErrors

          const recordKeysWithErrors = extractRecordKeysWithErrors(rawErrors)
          this.records = this.records.map(r => ({
            ...r,
            isRecordWithError: recordKeysWithErrors.has(r.key),
          })) as Array<DomainManagementRecord>
        }

        throw e
      }
    },
  },
})
